import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Eye, EyeOff, Phone, User, Lock, KeyRound } from 'lucide-react';

export default function AuthPage() {
  const navigate = useNavigate();
  const { isAgeVerified, verifyAge, login, register, verifySMS } = useAuth();
  const { t } = useLanguage();
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [guidelinesConfirm, setGuidelinesConfirm] = useState(false);
  const [mode, setMode] = useState('login'); // login | register | verify
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleAgeVerify = () => {
    if (ageConfirm && guidelinesConfirm) verifyAge();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const result = await login(phone, password);
      if (!result.user.is_verified) {
        setMode('verify');
        setInfo('Account not verified. Enter the SMS code to continue.');
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const result = await register(phone, password, username);
      if (result.requires_verification) {
        setMode('verify');
        setInfo('Registration successful! An SMS code has been sent. Ask your admin for the code.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await verifySMS(phone, smsCode);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  if (!isAgeVerified) {
    return (
      <div data-testid="age-gate" className="min-h-screen bg-[#050108] flex items-center justify-center px-6">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#FF007F]/10 rounded-full blur-[120px]" />
        <div className="relative z-10 glass-card rounded-3xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF007F]/20 to-[#E0115F]/20 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#FF007F]" />
          </div>
          <h2 className="font-display text-2xl font-bold mb-3">{t('ageTitle')}</h2>
          <p className="text-slate-400 text-sm mb-8">{t('ageSub')}</p>
          <div className="space-y-4 text-left mb-8">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input data-testid="age-confirm-checkbox" type="checkbox" checked={ageConfirm} onChange={() => setAgeConfirm(!ageConfirm)} className="w-5 h-5 rounded accent-[#FF007F]" />
              <span className="text-sm text-slate-300">{t('iConfirm')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input data-testid="guidelines-confirm-checkbox" type="checkbox" checked={guidelinesConfirm} onChange={() => setGuidelinesConfirm(!guidelinesConfirm)} className="w-5 h-5 rounded accent-[#FF007F]" />
              <span className="text-sm text-slate-300">{t('iAgree')}</span>
            </label>
          </div>
          <button data-testid="enter-platform-btn" onClick={handleAgeVerify} disabled={!ageConfirm || !guidelinesConfirm}
            className="w-full gradient-button rounded-full py-3 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
            {t('enterPlatform')}
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'verify') {
    return (
      <div data-testid="verify-page" className="min-h-screen bg-[#050108] flex items-center justify-center px-6">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#FF007F]/10 rounded-full blur-[120px]" />
        <div className="relative z-10 glass-card rounded-3xl p-10 max-w-md w-full">
          <div className="text-center mb-6">
            <KeyRound className="w-10 h-10 text-[#FF007F] mx-auto mb-3" />
            <h2 className="font-display text-2xl font-bold">SMS Verification</h2>
            <p className="text-slate-400 text-sm mt-2">{info}</p>
          </div>
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Verification Code</label>
              <input data-testid="sms-code-input" type="text" value={smsCode} onChange={e => setSmsCode(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-center text-2xl tracking-[0.5em] placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50"
                placeholder="000000" maxLength={6} required />
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <button data-testid="verify-btn" type="submit" disabled={loading || smsCode.length < 6}
              className="w-full gradient-button rounded-full py-3 font-semibold disabled:opacity-50">
              {loading ? '...' : 'Verify Account'}
            </button>
          </form>
          <button onClick={() => setMode('login')} className="w-full mt-4 text-sm text-slate-400 hover:text-white text-center">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="auth-page" className="min-h-screen bg-[#050108] flex items-center justify-center px-6">
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#FF007F]/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-[#4B0082]/20 rounded-full blur-[120px]" />
      <div className="relative z-10 glass-card rounded-3xl p-10 max-w-md w-full">
        <h2 className="font-display text-3xl font-bold text-center mb-2">FlirtyLive</h2>
        <p className="text-slate-400 text-center text-sm mb-8">{mode === 'login' ? t('login') : t('signup')}</p>

        <div className="flex gap-2 mb-6">
          <button data-testid="login-tab" onClick={() => { setMode('login'); setError(''); }}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === 'login' ? 'gradient-button' : 'bg-white/5 hover:bg-white/10'}`}>
            {t('login')}
          </button>
          <button data-testid="signup-tab" onClick={() => { setMode('register'); setError(''); }}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${mode === 'register' ? 'gradient-button' : 'bg-white/5 hover:bg-white/10'}`}>
            {t('signup')}
          </button>
        </div>

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
              <Phone className="w-3 h-3" /> {mode === 'login' ? 'Phone or Username' : 'Phone Number'}
            </label>
            <input data-testid="phone-input" type="text" value={phone} onChange={e => setPhone(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50"
              placeholder={mode === 'login' ? 'Phone or username' : '+39 333 1234567'} required />
          </div>
          {mode === 'register' && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                <User className="w-3 h-3" /> {t('username')}
              </label>
              <input data-testid="username-input" type="text" value={username} onChange={e => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50"
                placeholder="Choose a username" required minLength={3} />
            </div>
          )}
          <div className="relative">
            <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
              <Lock className="w-3 h-3" /> {t('password')}
            </label>
            <input data-testid="password-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50 pr-12"
              placeholder={t('password')} required />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-8 text-slate-400 hover:text-white">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {error && <p data-testid="auth-error" className="text-red-400 text-sm">{error}</p>}
          <button data-testid="auth-submit-btn" type="submit" disabled={loading}
            className="w-full gradient-button rounded-full py-3 font-semibold disabled:opacity-50">
            {loading ? '...' : mode === 'login' ? t('login') : t('signup')}
          </button>
        </form>
      </div>
    </div>
  );
}
